import React, { useEffect, useState } from 'react';
import { Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { deleteDoc, doc, getDoc, getFirestore } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UniformRequest, UniformRequestStatus, UniformRequestType } from '../../../types/UniformRequest';
import uniformRequestConverter from '../../../converters/uniformRequestConverter';
import { EditRounded, DeleteRounded, RunCircleRounded, CheckCircleRounded, MopedRounded } from '@mui/icons-material';
import { VacationRequestInfoBox } from '../../vacation/VacationRequestInfoBox';
import { UniformRequestInfoBox } from './UniformRequestInfoBox';
import { updateUniformRequestStatus, updateUniformRequestType } from '../../../utils/updateUniformRequest';
import { VacationRequestForm } from '../../vacation/VacationRequestForm';
import { UnifromRequestForm } from './UniformRequestForm';
import { Employee } from '../../../types/Employee';
import { ROLES_PERMISSIONS_MAPPING } from '../../../security/Role';
import { SecureComponent } from '../../../security/SecureComponent';

export const UniformRequestPage = () => {
  const db = getFirestore();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { state } = useLocation();

  const [uniformRequest, setUniformRequest] = useState<UniformRequest>((state as any)?.uniformRequest ?? undefined);
  const [disableDelete, setDisableDelete] = useState(false);

  const [type, setType] = useState<UniformRequestType>(uniformRequest ? uniformRequest.type : 'Complete Uniform');
  const [showUniformRequestForm, setShowUniformRequestForm] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  
  const updateStatus = (status: UniformRequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updateUniformRequestStatus(uniformRequest, status).then(() => {
        getDoc(doc(db, `Employees/${uniformRequest.idNumber}/UniformRequests/${id}`).withConverter(uniformRequestConverter)).then(docSnapshot => {
          if (docSnapshot.exists()) {
            setUniformRequest(docSnapshot.data());
          }
        });
        enqueueSnackbar(`Uniform request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  const handleDeleteOnClick = () => {
    if (window.confirm('Are you sure you want to delete this uniform request?')) {
      setDisableDelete(true);
      deleteDoc(doc(db, `Employees/${uniformRequest.idNumber}/UniformRequests/${uniformRequest.id}`))
        .then(() => {
          enqueueSnackbar('Uniform request has been deleted.', { variant: 'success' });
        })
        .finally(() => {
          setDisableDelete(false);
          setTimeout(() => navigate(-1), 250);
      });
    }
  };

  const handleSaveUniformRequestOnClick = () => {
    if (window.confirm(`Save uniform request changes?`)) {
      setDisableSaveButton(true);
      updateUniformRequestType(uniformRequest, type)
      .then(() => {
        setUniformRequest({ ...uniformRequest, type });
        hideUniformRequestForm();
        enqueueSnackbar('Uniform request type has been update.', { variant: 'success' });
      });
    }
  };

  const handleUniformRequestOnChanged = (_e: Employee, t: UniformRequestType) => {
    setType(t);
    setDisableSaveButton(uniformRequest.type === t);
  };

  const openUniformRequestForm = () => setShowUniformRequestForm(true);
  const hideUniformRequestForm = () => setShowUniformRequestForm(false);
  const handleSetProcessingOnClick = () => updateStatus('Processing');
  const handleSetAvailableOnClick = () => updateStatus('Available');
  const handleSetCompletedOnClick = () => updateStatus('Completed');

  useEffect(() => {
    if (!state || !(state as any)?.uniformRequest) {
      getDoc(doc(db, `UniformRequests/${id}`).withConverter(uniformRequestConverter)).then(docSnapshot => {
        if (docSnapshot.exists()) {
          setUniformRequest(docSnapshot.data());
        }
      });
    }
  }, []);

  useEffect(() => {
    if (uniformRequest) {
      setType(uniformRequest.type);
    }
  }, [uniformRequest]);

  if (!uniformRequest) {
    return (
      <Typography variant="caption">Please wait...</Typography>
    )
  }
  
  return (
    <Container disableGutters>
      <Stack spacing={3} direction="row" alignItems="center" mb={2}>
        <Chip label={uniformRequest.idNumber} />
        <Typography variant="h6">{uniformRequest.name}</Typography>
        <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.uniform']}>
          <Stack direction='row' spacing={1} sx={{ display: 'flex', flexGrow: 1, justifyContent: 'right' }}>
            <Button startIcon={<EditRounded />} color="warning" onClick={openUniformRequestForm}>EDIT</Button>
            <Button startIcon={<DeleteRounded />} color="error" disabled={disableDelete} onClick={handleDeleteOnClick}>DELETE</Button>
          </Stack>
        </SecureComponent>
      </Stack>
      <Paper>
        <UniformRequestInfoBox uniformRequest={uniformRequest} />
      </Paper>
      <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.uniform']}>
        <>
          <Stack mt={1} spacing={1} direction="row" justifyContent="right">
            <Button 
              color="warning" 
              startIcon={<RunCircleRounded />} 
              disabled={uniformRequest.status !== 'New'}
              onClick={handleSetProcessingOnClick}
            >
              Processing
            </Button>
            <Button 
              color="secondary" 
              startIcon={<MopedRounded />}
              disabled={uniformRequest.status !== 'New' && uniformRequest.status !== 'Processing'}
              onClick={handleSetAvailableOnClick}
            >
              For Pickup
            </Button>
            <Button 
              color="success" 
              startIcon={<CheckCircleRounded />} 
              disabled={uniformRequest.status === 'Completed' || uniformRequest.status === 'Cancelled'}
              onClick={handleSetCompletedOnClick}
            >
              Completed
            </Button>
          </Stack>
          <Dialog open={showUniformRequestForm} onClose={() => setShowUniformRequestForm(false)} fullWidth>
            <DialogTitle>Update Vacation Request</DialogTitle>
            <DialogContent dividers>
              <UnifromRequestForm uniformRequest={uniformRequest} onChanged={handleUniformRequestOnChanged} />
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={hideUniformRequestForm}>CANCEL</Button>
              <Button color="primary" onClick={handleSaveUniformRequestOnClick} disabled={disableSaveButton}>SAVE</Button>
            </DialogActions>
          </Dialog>
        </>
      </SecureComponent>
    </Container>
  )
};